import { ReactElement, useEffect, useState } from "react";
import { EaseToOptions } from "mapbox-gl";
import { CustomLayer } from "../../services/map/interfaces/custom-layer";
import { useDependency } from "../../contexts/DependencyContext";
import { IComponent } from "../../services/http/component/models/IComponent";
import { MapService } from "../../services/map/MapService";
import { layers } from "../../services/map/layers";
import MapBase from "../../components/Map/MapBase";
import MapMarkers from "./MapMarkers";
import MapSidebar from "./MapSidebar";
import Layer from "../../components/Map/Layer";
import styles from "./MapPage.module.scss";
import { useRecurrentAsyncCall } from "../../hooks/UseAsyncCall";
import useWindowWidth from "../../hooks/UseWindowWidth";
import { ComponentService } from "../../services/http/component/ComponentService";
import { useTimeMachineUpdate } from "../../contexts/TimeMachineContext";
import MapPopup from "../../components/Map/MapPopup";

async function loadData(service: ComponentService): Promise<IComponent[]>
{
    return await service.getComponentsWithCoordinates();
}

const MapPage = (): ReactElement =>
{
    const isDesktop = useWindowWidth(992);
    const mapService = useDependency(MapService);
    const componentModelService = useDependency(ComponentService);
    const [currentComponent, setCurrentComponent] = useState<IComponent>();
    const timeMachineUpdate = useTimeMachineUpdate();

    const [popupComponent, setPopupComponent] = useState<IComponent | null>(null);
    const [popupPosition, setPopupPosition] = useState<{ x: number; y: number } | null>(null);
    
    let { loading, content } = useRecurrentAsyncCall(parseInt(process.env.REACT_APP_CHART_REFRESH_RATE ?? "300000"), loadData, componentModelService);

    async function handleComponentChange(component?: IComponent)
    {
        setCurrentComponent(component);

        if (component && component.longitude && component.latitude && mapService.map) {
            const point = mapService.map.project([component.longitude, component.latitude]);
            setPopupPosition({ x: point.x, y: point.y });
            setPopupComponent(component);
        } else {
            setPopupComponent(null);
            setPopupPosition(null);
        }
    }

    useEffect(() =>
    {
        timeMachineUpdate(undefined!);
        if (currentComponent && currentComponent.longitude && currentComponent.latitude)
        {
            const offSet = {
                longOffset: isDesktop ? 0.15 : 0,
                latOffset: isDesktop ? 0 : -0.1,
            }
            mapService.setPosition({
                center: [currentComponent.longitude + offSet.longOffset, currentComponent.latitude + offSet.latOffset],
                zoom: 9
            } as EaseToOptions);
        }
    }, [currentComponent, mapService, isDesktop, timeMachineUpdate]);

    return (
        <main className={styles.map}>
            <MapBase withThemeSelector={true} />

            {loading && <div className="loading" />}

            {!loading && <>
                {layers.map((layer: CustomLayer) => (
                    <Layer key={layer.id} {...{ layer }} />
                ))}

                <MapMarkers
                    markers={content}
                    current={currentComponent}
                    setCurrent={handleComponentChange}
                />

                {currentComponent && (
                    <MapSidebar
                        component={currentComponent}
                        close={() => handleComponentChange()}
                    />
                )}

                
            </>}
        </main>
    );
};

export default MapPage;
